import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AUTH_API, apiUrl } from '@/config/api';
import { useAuth } from '@/contexts/AuthContext';
import { getAuthToken } from '@/utils/auth';
import { oaAppPath } from './paths';
import { OaWorkspaceShell } from '../components/OaWorkspaceShell';
import { 
  Mail, 
  User, 
  Phone, 
  Trash2, 
  LogOut, 
  Home, 
  UserCheck 
} from 'lucide-react';

function authHeaders() {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export function OncoAgentSettings(): React.ReactElement {
  const { user, updateUser, logout, isAuthenticated } = useAuth();
  const [name, setName] = useState(String(user?.full_name || user?.name || ''));
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [typed, setTyped] = useState('');
  const [askDelete, setAskDelete] = useState(false);

  useEffect(() => {
    document.title = 'Account · OncoAgent';
    setName(String(user?.full_name || user?.name || ''));
    
    let rawPhone = String(user?.phone || '').trim();
    if (!rawPhone || rawPhone === '910000000000' || rawPhone === '+910000000000' || rawPhone.includes('0000000')) {
      rawPhone = '+1 (555) 019-2834'; // High-fidelity clinical default placeholder number
    }
    setPhone(rawPhone);
  }, [user]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextName = name.trim();
    if (!nextName) {
      setNote('Add your name so referrals carry the right clinician.');
      return;
    }
    setBusy('save');
    setNote('');
    try {
      const { data } = await axios.put(
        `${AUTH_API}/profile`,
        { full_name: nextName, phone: phone.trim() || undefined },
        { withCredentials: true, headers: authHeaders() }
      );
      updateUser(data);
      setNote('Profile saved. This account stays linked on this device and on the server.');
    } catch (err: any) {
      setNote(err?.response?.data?.detail || err?.message || 'Could not save profile.');
    } finally {
      setBusy('');
    }
  };

  const deleteAccount = async () => {
    if (typed !== 'Delete' || busy) return;
    setBusy('delete');
    setNote('');
    try {
      const response = await fetch(apiUrl('/api/me/delete'), {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setAskDelete(false);
        setTyped('');
        setNote(
          response.status === 423
            ? 'This account is on a legal hold. Email care@getbyonco.com.'
            : data?.detail || data?.message || 'Deletion failed.'
        );
        return;
      }
      await logout('/oncoagent');
    } catch {
      setNote('Deletion failed. Try again, or email care@getbyonco.com.');
    } finally {
      setBusy('');
    }
  };

  return (
    <OaWorkspaceShell
      kicker="Account"
      title="Your OncoAgent profile"
      subtitle={`Sign-in is tied to ${user?.email || 'this ByOnco account'}. Edit how you appear on referrals, then keep working from Home.`}
    >

      <form className="oa-ws-panel space-y-4" onSubmit={save}>
        <h2>Edit profile</h2>
        
        <div>
          <label className="oa-ws-label flex items-center gap-1.5 mb-1.5" htmlFor="oa-acc-email">
            <Mail className="w-3.5 h-3.5 text-[#6d6a63]" />
            <span>Email</span>
          </label>
          <input id="oa-acc-email" className="oa-ws-input bg-gray-50/50 cursor-not-allowed text-gray-500" value={user?.email || ''} disabled />
        </div>

        <div>
          <label className="oa-ws-label flex items-center gap-1.5 mb-1.5" htmlFor="oa-acc-name">
            <User className="w-3.5 h-3.5 text-[#6d6a63]" />
            <span>Full name</span>
          </label>
          <input
            id="oa-acc-name"
            className="oa-ws-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Dr. Jane Smith"
            autoComplete="name"
          />
        </div>

        <div>
          <label className="oa-ws-label flex items-center gap-1.5 mb-1.5" htmlFor="oa-acc-phone">
            <Phone className="w-3.5 h-3.5 text-[#6d6a63]" />
            <span>Phone</span>
          </label>
          <input
            id="oa-acc-phone"
            className="oa-ws-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 (555) 019-2834"
            autoComplete="tel"
          />
        </div>

        <div className="oa-ws-actions pt-2">
          <button className="oa-ws-btn oa-ws-btn-primary" type="submit" disabled={!!busy}>
            {busy === 'save' ? 'Saving…' : 'Save profile'}
          </button>
        </div>
        {note ? <p className="text-xs text-[#0f3e17] bg-emerald-50 px-3 py-2 rounded-lg mt-2" style={{ marginTop: 12 }}>{note}</p> : null}
      </form>

      <div className="oa-ws-panel">
        <h2>Session</h2>
        <p className="oa-ws-muted text-xs leading-relaxed mb-4">
          {isAuthenticated
            ? 'You are signed in securely. Sign out on shared clinic computers.'
            : 'Sign in to sync this workspace across active clinician registries.'}
        </p>
        <div className="oa-ws-actions flex gap-3">
          <button
            type="button"
            className="oa-ws-btn oa-ws-btn-ghost flex items-center gap-1.5"
            onClick={() => logout('/oncoagent')}
          >
            <LogOut className="w-4 h-4 text-[#6d6a63]" />
            <span>Sign out</span>
          </button>
          <a className="oa-ws-btn oa-ws-btn-ghost flex items-center gap-1.5" href={oaAppPath()}>
            <Home className="w-4 h-4 text-[#6d6a63]" />
            <span>Back to Home</span>
          </a>
        </div>
      </div>

      <div className="oa-ws-panel">
        <h2 className="text-rose-800">Delete account</h2>
        <p className="oa-ws-muted text-xs leading-relaxed mb-4">
          Removes your ByOnco records from this login. Clinic packets already sent stay securely with the receiving clinic.
        </p>
        {!askDelete ? (
          <div className="oa-ws-actions">
            <button 
              type="button" 
              className="oa-ws-btn oa-ws-btn-ghost flex items-center gap-1.5 text-rose-700 hover:bg-rose-50/50 border-rose-200" 
              onClick={() => setAskDelete(true)}
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete account</span>
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <label className="oa-ws-label flex items-center gap-1.5 text-rose-900 font-semibold" htmlFor="oa-acc-del">
              <Trash2 className="w-3.5 h-3.5" />
              <span>Type "Delete" to confirm permanent account deletion</span>
            </label>
            <input
              id="oa-acc-del"
              className="oa-ws-input border-rose-200 focus:border-rose-400 focus:ring-rose-200/50"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Delete"
              autoComplete="off"
            />
            <div className="oa-ws-actions flex gap-3 pt-1">
              <button 
                type="button" 
                className="oa-ws-btn oa-ws-btn-ghost flex items-center gap-1.5" 
                onClick={() => { setAskDelete(false); setTyped(''); }}
              >
                <UserCheck className="w-4 h-4 text-emerald-700" />
                <span>Keep account</span>
              </button>
              <button
                type="button"
                className="oa-ws-btn oa-ws-btn-primary flex items-center gap-1.5 bg-rose-700 hover:bg-rose-800 text-white border-transparent disabled:opacity-50"
                style={{ color: '#ffffff' }}
                disabled={typed !== 'Delete' || !!busy}
                onClick={deleteAccount}
              >
                <Trash2 className="w-4 h-4 text-white" />
                <span>{busy === 'delete' ? 'Deleting…' : 'Delete permanently'}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </OaWorkspaceShell>
  );
}

export default OncoAgentSettings;
